import _ from 'lodash';
import * as actions from '../actions';
import * as actionTypes from '../constants/actionTypes';
import { getApiConfig } from '../utilities';
import {hashHistory} from 'react-router';

let {get, post} = getApiConfig('FeatureInclusion/');

const dispatchPromise = (dispatchFunction) => {
  return new Promise((resolve, reject) => {
    try {
      resolve(dispatchFunction());
    } catch(ex) {
      reject(ex);
    }
  });
}

const catchErrorMessage = (response) => {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      if(!response.success && !response.__error__) {
        dispatch(actions.messageBox_Show(true, "error", "Error Occurred Processing Request", (response.message || []).join('<br />'), [{
          text: 'OK',
          onClick: () => {
            dispatch(actions.messageBox_Show());
            reject(response);
          }
        }]));
      } else if(response.__error__) {
        reject(response);
        hashHistory.push('/login');
      } else if(response.message.length > 0 && response.success) {
        dispatch(actions.messageBox_Show(true, 'system', 'Success', response.message.join('<br />'), [{
          text: "OK",
          onClick: () => {
            dispatch(actions.messageBox_Show());
            resolve(response);
          }
        }]));
      } else {
        resolve(response);
      }
    });
  }
}

/**
 * Load Feature Inclusion preset
 */
export function loadPreset() {
  return (dispatch, getState) => {
    const { auth, asCustomer } = getState();
    return new Promise((resolve, reject) => {
      let fabricatorId = asCustomer.data ? asCustomer.data.fabricatorId : auth.fabricatorId;
      get("GetPreset?fabricatorId=" + fabricatorId)
      .then(result => dispatch({
        type: actionTypes.FEATURE_INCLUSION_FABRICATOR_PRESET_LOADED,
        payload: {
          preset: result.responses[0]
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Load Feature Inclusion Presets
 */
export function loadPresets() {
  return (dispatch, getState) => {
    const { auth } = getState();
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch(actions.loadingDialog_show(true, 'Loading Features...', 'Loading Features and Inclusion Data... Please Wait...')))
        .then(() => get("GetPresets?masterId=" + auth.masterFabricatorId))
        .then(result => dispatch(catchErrorMessage(result)))
        .then(result => dispatch({
          type: actionTypes.FEATURE_INCLUSION_LOADED,
          payload: { 
            featureList: result.responses[0],
            fabricatorPresetData: result.responses[1]
          }
        }))
        .then(() => dispatch(actions.loadingDialog_show(false)))
        .then(resolve)
        .catch(reject);
    }).catch(console.error);
  }
};

/**
 * Load Customer/Fabricator Data for Assignee Popup
 */
export function loadCustomers() {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch(actions.loadingDialog_show(true, 'Loading Fabricators...', 'Loading Fabricators List... Please Wait...')))
        .then(() => get("GetCustomers"))
        .then(result => dispatch(catchErrorMessage(result)))
        .then(result => dispatch({
          type: actionTypes.FEATURE_INCLUSION_CUSTOMERS_LOADED,
          payload: result.responses
        }))
        .then(() => dispatch(actions.loadingDialog_show(false)))
        .then(resolve)
        .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Create new Feature Inclusion Preset
 * @param {string} name 
 * @param {string} description 
 */
export function createPreset(name, description) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      let data = {
        name,
        description
      };
      dispatchPromise(() => dispatch(actions.loadingDialog_show(true, 'Saving...', 'Creating a new Feature Inclusion Preset...')))
        .then(() => post(data, "CreatePreset"))
        .then(result => dispatch(catchErrorMessage(result)))
        .then(response => dispatch({
          type: actionTypes.FEATURE_INCLUSION_PRESET_CREATED,
          payload: { id: response.responses[0].id, ...data }
        }))
        .then(dispatch(actions.loadingDialog_show(false)))
        .then(resolve)
        .catch(reject)
    }).catch(console.error);
  }
}

export function updatePresetValue(featureInclusionPresetId, key, value) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      let data = {
        featureInclusionPresetId,
        key,
        value
      };
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_VALUE_UPDATE,
        payload: {...data}
      }))
      .then(() => post(data, "UpdatePreset"))
      .then(result => dispatch(catchErrorMessage(result)))
      .then(resolve)
      .catch(reject);
    });
  }
}

/**
 * Delete Feature Inclusion Preset
 * @param {string (GUID)} featureInclusionPresetId 
 */
export function deletePreset(featureInclusionPresetId) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      let data = {
        featureInclusionPresetId
      };
      dispatch(actions.messageBox_Show(true, 'system', 'Are you sure?', "Are you sure you wish to delete this Feature Inclusion Profile? This action cannot be undone.", [{
        text: "Delete",
        onClick: () => {
          post(data, "DeletePreset")
            .then(result => dispatch(catchErrorMessage(result)))
            .then(dispatch(actions.messageBox_Show()))
            .then(dispatch({
              type: actionTypes.FEATURE_INCLUSION_PRESET_DELETED,
              payload: {...data}
            }))
            .then(resolve)
            .catch(reject);
        }
      }, {
        text: "Cancel",
        onClick: () => {
          dispatch(actions.messageBox_Show());
          reject();
        }
      }]));
    }).catch(console.error);
  }
}

/**
 * Enable/Disable Feature Inclusion Preset Feature Group
 * @param {string (GUID)} featureInclusionPresetId 
 * @param {string} featureGroup 
 * @param {bool} enabled 
 * @param {bool} overrideFeatureEnabled 
 */
export function enableFeatureGroup(featureInclusionPresetId, featureGroup, enabled, overrideFeatureEnabled) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      let data = {
        featureInclusionPresetId,
        featureGroup,
        enabled,
        overrideFeatureEnabled
      };
      post(data, "EnableFeatureGroup")
      .then(dispatch({
        type: actionTypes.FEATURE_INCLUSION_PRESET_ENABLE_GROUP,
        payload: data
      }))
      .then(result => dispatch(catchErrorMessage(result)))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Enable/Disable Feature in a Feature Group in a Feature Inclusion Preset
 * @param {string (GUID)} featureInclusionPresetId 
 * @param {int} featureId 
 * @param {bool} enabled 
 */
export function enableFeature(featureInclusionPresetId, featureId, enabled) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      let data = {
        featureInclusionPresetId,
        featureId,
        enabled
      };
      post(data, 'EnableFeature')
      .then(dispatch({
        type: actionTypes.FEATURE_INCLUSION_FEATURE_ENABLE,
        payload: data
      }))
      .then(result => dispatch(catchErrorMessage(result)))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Add Assignee to Feature Inclusion Preset
 * @param {string (GUID)} featureInclusionPresetId 
 * @param {int} customerId 
 */
export function addAssignee(featureInclusionPresetId, customerId) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      let data = {
        featureInclusionPresetId,
        customerId
      };
      post(data, 'AddAssignee')
      .then(dispatch({
        type: actionTypes.FEATURE_INCLUSION_ASSIGNEE_ADDED,
        payload: data
      }))
      .then(result => dispatch(catchErrorMessage(result)))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Remove Assignee to Feature Inclusion Preset
 * @param {string (GUID)} featureInclusionPresetId 
 * @param {int} customerId 
 */
export function removeAssignee(featureInclusionPresetId, customerId) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      let data = {
        featureInclusionPresetId,
        customerId
      };
      post(data, 'RemoveAssignee')
      .then(dispatch({
        type: actionTypes.FEATURE_INCLUSION_ASSIGNEE_REMOVED,
        payload: data
      }))
      .then(result => dispatch(catchErrorMessage(result)))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Show/Hide Add New Preset Popup
 * @param {bool} show 
 */
export function showAddNewPresetPopup(show = false) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SHOW_ADD_PRESET,
        payload: {
          show
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Show/Hide Add New Assignee Popup
 * @param {bool} show 
 */
export function showAddAssigneePopup(show = false) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SHOW_ADD_ASSIGNEE,
        payload: {
          show
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Set Feature Inclusion Page
 * @param {int} page 
 */
export function setPage(page = 0) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_PAGE_CHANGE,
        payload: {
          page
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Set Feature Inclusion Page
 * @param {int} page 
 */
export function setFeaturePage(page = 0) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SELECTION_PAGE_CHANGE,
        payload: {
          page
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Select Preset by Preset ID
 * @param {string} id 
 * @param {bool} setOnly
 */
export function selectPreset(id, setOnly = false) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SET_SELECTED_PRESET,
        payload: {
          selectedPreset: id,
          setOnly
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Set Feature Inclusion Search Term
 * @param {string} term 
 */
export function setSearchTerm(term) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SET_SEARCH_TERM,
        payload: {
          searchTerm: term
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Set Assignee Popup Search Term
 * @param {string} term 
 */
export function setAssigneeSearchTerm(term) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_ASSIGNEE_SET_SEARCH_TERM,
        payload: {
          searchTerm: term
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Set Assignee Popup Page
 * @param {int} page 
 */
export function setAssigneePage(page) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_ASSIGNEE_SET_PAGE,
        payload: {
          page
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Toggle Assignee Check Mark on Assignee Popup
 * @param {int} id 
 */
export function checkAssignee(id) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_ASSIGNEE_SET_CHECK,
        payload: {
          customer_id: id
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * save assignees for preset
 */
export function saveAssignees() {
  return (dispatch, getState) => {
    const { featureInclusion } = getState();
    return new Promise((resolve, reject) => {
      let data = {
        featureInclusionPresetId: featureInclusion.selectedPreset,
        customerIds: featureInclusion.assign_checked
      }
      post(data, "SaveAssignees")
      .then(result => dispatch(catchErrorMessage(result)))
      .then(result => {
        if(result.message.length > 0 && result.success) {
          return dispatch(loadPresets()); //Assignee was moved from another preset, we need to load an updated preset list now.
        } else {
          return dispatchPromise(dispatch({
            type: actionTypes.FEATURE_INCLUSION_ASSIGNEES_SAVED,
            payload: {
              featureInclusionPresetId: data.featureInclusionPresetId,
              assignees: result.responses
            }
          }));
        }
      })
      .then(resolve)
      .catch(reject);
    }).catch(console.log);
  }
}

/**
 * Toggle selection of Feature Group
 * @param {string} featureGroup 
 */
export function toggleFeatureGroup(featureGroup) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SELECTION_CHECK_FEATURE_GROUP,
        payload: {
          featureGroup
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Set Feature Selection Search Term
 * @param {string} term 
 */
export function setFeatureSearchTerm(term) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SELECTION_SET_SEARCH_TERM,
        payload: {
          term
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  } 
}

/**
 * Toggle Feature ID Selection
 * @param {object} feature 
 */
export function selectFeature(feature) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SELECTION_CHECK_FEATURE,
        payload: {
          feature
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Select Feature Group
 * @param {string} featureGroup 
 */
export function selectFeatureGroup(featureGroup) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      dispatchPromise(() => dispatch({
        type: actionTypes.FEATURE_INCLUSION_SELECTION_SELECT_FEATURE_GROUP,
        payload: {
          featureGroup
        }
      }))
      .then(resolve)
      .catch(reject);
    }).catch(console.error);
  }
}

/**
 * Save Feature Selection options
 */
export function saveFeatureSelection() {
  return (dispatch, getState) => {
    const state = getState();
    const { featureInclusion } = state;
    const { featureSelection } = featureInclusion;
    let featureInclusionPresetId = featureInclusion.selectedPreset;
    var checkedFeatureIds = [];
    for(var i = 0; i < featureSelection.checkedFeatures.length; i++) {
      let checkedFeature = featureSelection.checkedFeatures[i];
      for(var n = 0; n < checkedFeature.featureIds.length; n++) {
        if(_.indexOf(checkedFeatureIds, checkedFeature.featureIds[n]) == -1) {
          checkedFeatureIds.push(checkedFeature.featureIds[n]);
        }
      }
    }
    return new Promise((resolve, reject) => {
      let data = {
        checkedFeatureGroups: featureSelection.checkedFeatureGroups,
        checkedFeatureIds,
        featureInclusionPresetId
      };
      dispatchPromise(() => dispatch(actions.loadingDialog_show(true, 'Saving...', "Saving Feature Selections, this may take a moment...")))
        .then(() => post(data, "SaveFeatures"))
        .then(result => dispatch(catchErrorMessage(result)))
        .then(() => hashHistory.push('/featureInclusion'))
        .then(resolve)
        .catch(reject);
    }).catch(console.error);
  }
}